import { TableItem } from 'components/atoms';
import { FormInput, LabeledStatus } from 'components/molecules';
import { QueryFilter, Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useComponentVisible, useHasPermission } from 'hooks';
import { useInfiniteTable } from 'hooks/useInfiniteTable';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useRowSelect } from '@table-library/react-table-library/select';
import { Action, State } from '@table-library/react-table-library/types/common';
import { useGetContracts } from 'services/contract';
import { IContractsListSearch } from 'services/contract/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { formatDate } from 'utils/global/date.utils';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { useSearchById, useSetSearchById } from 'store/state';

enum COLUMNS {
  title,
  label,
  startDate,
  endDate,
  status,
}

const contractSearchInitialValues: IContractsListSearch = {
  contractTitle: '',
  labelName: '',
};

const Contracts = () => {
  usePermissionGuard('ContractPermissionsLIST**', '/', true, 'contracts');
  const hasPermission = useHasPermission();

  const { t } = useTranslation();
  const navigate = useNavigate();

  const columnsVisibility = useTableColumnsVisibility('Contracts', COLUMNS);

  const i18nextPrefix = 'contract';
  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false, true, columnsVisibility.tableId);

  const searchById = useSearchById();
  const setSearchById = useSetSearchById();
  const filters = searchById[columnsVisibility.tableId] || contractSearchInitialValues;

  const contracts = useGetContracts({ ...filters });

  const tableProps = useInfiniteTable(contracts, { selectable: false });

  const tableData = {
    nodes: tableProps.data.nodes.map(data => ({
      ...data,
      id: `/label/${data.labelId}/contract/${data.id}/form`,
    })),
  };

  const hasDetailPermission = hasPermission('ContractPermissionsLIST**', 128);

  const onSelectChange = (action: Action, state: State) => {
    if (!hasDetailPermission) {
      return;
    }
    
    const id: string = state.id ?? state.ids[state.ids.length - 1];
    navigate(id, { state: 'contracts' });
  };

  const select = useRowSelect(tableData, {
    onChange: onSelectChange,
  });

  const tableSelect = hasDetailPermission ? select : undefined;

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={t(`navbar.contracts`)}
          path=''
          hideBackButton
          topbarContent={
            <div className='flex'>
              <QueryFilter.ButtonOpener
                i18nextPrefix={''}
                onClick={handleComponentVisible}
                variant={BUTTON_VARIANT.default}
                isClear
              />

              <Table.CustomColumnsButton
                columns={COLUMNS}
                isClear
                visibility={columnsVisibility}
                customI18next=''
                i18nextPrefix={'input.label'}
              />
            </div>
          }>
          <QueryFilter<IContractsListSearch>
            i18nextPrefix={''}
            initialValues={filters}>
            <>
              <QueryFilter.Form<IContractsListSearch>
                ref={ref}
                isOpen={isComponentVisible}
                onDebouncedFormChange={values => setSearchById(columnsVisibility.tableId, values)}>
                <QueryFilter.InputsGrid>
                  <FormInput.Text
                    name='contractTitle'
                    label={t('input.label.contract')}
                  />
                  <FormInput.Text
                    name='labelName'
                    label={t('input.label.label')}
                  />
                </QueryFilter.InputsGrid>
              </QueryFilter.Form>
              <QueryFilter.Labels isFormOpen={isComponentVisible} />
            </>
          </QueryFilter>
        </TopBar>
      }>
      <Table
        id={columnsVisibility.tableId}
        loading={tableProps.loading}
        onFullyScroll={tableProps.onFullyScroll}
        select={tableSelect}
        displayMode
        data={tableData}
        columns={[
          {
            label: t(`input.label.title`),
            renderCell: ({ title }) => (
              <TableItem>
                <TableItem.Text text={title} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(COLUMNS.title),
          },
          {
            label: t(`input.label.label`),
            renderCell: ({ labelName }) => (
              <TableItem>
                <TableItem.Text text={labelName} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(COLUMNS.label),
          },
          {
            label: t('input.label.startDate'),
            renderCell: ({ startDate }) => (
              <TableItem>{formatDate(startDate)}</TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(COLUMNS.startDate),
          },
          {
            label: t('input.label.endDate'),
            renderCell: ({ endDate }) => (
              <TableItem>{endDate ? formatDate(endDate) : '-'}</TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(COLUMNS.endDate),
          },
          {
            label: t('input.label.status'),
            renderCell: ({ status }) => (
              <TableItem justifyPosition='center'>
                <LabeledStatus.Contract status={status} />
              </TableItem>
            ),
            hide: columnsVisibility.hide(COLUMNS.status),
          },
        ]}
      />
    </TopbarPage>
  );
};

export default Contracts;
